import { useEffect, useRef, useState } from "react";
import { Bot, Loader2, MessageCircle, Send, X, AlertTriangle } from "lucide-react";
import { API } from "../lib/api";

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => {
    let s = localStorage.getItem("td_chat_session");
    if (!s) {
      s = uid();
      localStorage.setItem("td_chat_session", s);
    }
    return s;
  });
  const [email, setEmail] = useState(() => localStorage.getItem("td_chat_email") || "");
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm the TrendDrop assistant. Ask me about your orders, shipping, returns or any product. For order questions, add your checkout email below." },
  ]);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    if (email) localStorage.setItem("td_chat_email", email);
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text, email: email || null }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let full = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const evt = JSON.parse(part.slice(6));
          if (evt.delta) {
            full += evt.delta;
            // keep last 10 chars buffered so the [ESCALATE] control token never flashes on screen
            const visible = full.slice(0, Math.max(0, full.length - 10)).replace("[ESCALATE]", "");
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: visible };
              return copy;
            });
          }
          if (evt.done) {
            const clean = full.replace("[ESCALATE]", "").trim();
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: clean };
              return copy;
            });
            if (evt.escalated) setEscalated(true);
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Connection issue — please try again." };
        return copy;
      });
    }
    setStreaming(false);
  };

  return (
    <>
      <button
        data-testid="chat-widget-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-amber-400 text-black shadow-[0_10px_40px_-10px_rgba(234,179,8,0.7)] transition-transform hover:scale-105 active:scale-95"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-24 right-6 z-50 flex h-[520px] w-[min(92vw,380px)] flex-col overflow-hidden rounded-2xl glass animate-fade-up"
        >
          <div className="flex items-center gap-3 border-b border-white/10 bg-white/[0.03] px-5 py-4">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-400/15 text-amber-300">
              <Bot size={18} />
            </span>
            <div>
              <p className="font-heading text-sm font-semibold text-white">TrendDrop Assistant</p>
              <p className="text-[11px] text-emerald-400">Online — answers instantly</p>
            </div>
          </div>
          {escalated && (
            <div data-testid="chat-escalated-banner" className="flex items-center gap-2 border-b border-amber-400/20 bg-amber-400/10 px-5 py-2 text-[11px] text-amber-200">
              <AlertTriangle size={13} /> Escalated to the shop owner — they'll follow up by email.
            </div>
          )}
          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-amber-400 text-black"
                      : "border border-white/10 bg-white/[0.05] text-slate-200"
                  }`}
                >
                  {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <div className="border-t border-white/10 p-3">
            <input
              data-testid="chat-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your order email (optional)"
              type="email"
              className="input-dark mb-2 !py-2 text-xs"
            />
            <div className="flex gap-2">
              <input
                data-testid="chat-message-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about orders, returns, products…"
                className="input-dark !py-2.5"
              />
              <button
                data-testid="chat-send-button"
                onClick={send}
                disabled={streaming}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-400 text-black transition-colors hover:bg-amber-300 disabled:opacity-50"
              >
                {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
